<template>
  <div class="promoCodeContainer">
    <member-content-title :title="$t('my_promo_title')" />
    <p class="inputTitle mb-2 wd-text-otempsm wd-mt-3" :class="[shouldInputErrorShow ? 'wd-text-red50' : 'wd-text-black50']">
      {{ $t('enter_promo_title') }}
    </p>
    <div id="inputArea" class="wd-mb-3">
      <v-text-field
        v-model="inputPromoCode"
        :placeholder="$t('enter_promo_code_placeholder')"
        :error="shouldInputErrorShow"
        :error-messages="errorMessage"
        color="orange50"
        bg-color="white"
        :hide-details="!shouldInputErrorShow"
        variant="outlined"
        density="compact"
        @update:model-value="handlePromoCodeChange"
        @keyup.enter="verifyPromoCode"
      />
      <v-btn
        :class="{ disable: shouldDisableClaimButton }"
        class="text-white inputArea__button"
        height="40"
        :color="shouldDisableClaimButton ? 'orange30' : 'orange50'"
        variant="flat"
        @click="verifyPromoCode"
      >
        <Loading v-if="isButtonLoading" />
        <span v-else>{{ $t('all_claim') }}</span>
      </v-btn>
    </div>

    <MemberTabs :tabs="tabs" />

    <div v-if="isSuccessHintShow" class="successHintBlock wd-flex wd-justify-between wd-items-center wd-pr-3 wd-pl-5 wd-py-4">
      <span class="wd-mr-3">{{ $t('claimed_coupon_success', { coupon_title: verifiedPromoCodeTitle }) }}</span>
      <v-btn class="wd-w-6 wd-h-6 !wd-bg-transparent" width="40" height="40" icon variant="text" @click="hideSuccessPromoHint">
        <funnow-icon class="wd-text-black" max-width="24" height="24" image-path="cancel-icon_white.svg" />
      </v-btn>
    </div>
  </div>
</template>

<script>
import * as memberService from '@/service/member'
import tools from '@/mixin/tools'
import { useBreakpoints } from '@/composition/useBreakpoints'
import { useUserInfo } from '@/composition/useMember'
import { useMemberPageAuth } from '@/composition/useMemberPageAuth'

import MemberContentTitle from '@/components/member/MemberContentTitle.vue'
import MemberEmptyState from '@/components/member/MemberEmptyState.vue'
import Loading from '@/components/icons/loading.vue'
import PromoCodeCard from '@/components/card/PromoCodeCard.vue'
import FunnowIcon from '@/components/icons/funnow-icon.vue'
import MemberTabs from '@/components/member/MemberTabs.vue'
import PromoCodeList from '@/components/member/PromoCodeList.vue'

export default defineNuxtComponent({
  name: 'PromoCodePage',
  components: {
    MemberContentTitle,
    MemberEmptyState,
    Loading,
    PromoCodeCard,
    FunnowIcon,
    MemberTabs,
    PromoCodeList,
  },
  mixins: [tools],
  setup() {
    const nuxtApp = useNuxtApp()
    const { $store: store, $auth, $axios, $cookiz } = nuxtApp
    const route = useRoute()
    const router = useRouter()
    const { t } = useI18n()
    const { userInfo } = useUserInfo()
    const { breakpoints } = useBreakpoints()
    const { xsAndDown: isMobile } = toRefs(breakpoints)

    definePageMeta({
      middleware: ['member-promo-middleware'],
      layout: 'member',
    })

    useMemberPageAuth(() => getPromoCodesAndAutoRedeem())

    const inputPromoCode = ref('')
    const activePromoCodes = ref([])
    const inactivePromoCodes = ref([])
    const isButtonLoading = ref(false)
    const shouldDisableClaimButton = computed(() => !inputPromoCode.value?.toString().trim().length || isButtonLoading.value)
    const verifiedPromoCodeTitle = ref('')
    const isSuccessHintShow = ref(false)
    const shouldInputErrorShow = ref(false)
    const errorMessage = ref('')

    const tabs = computed(() => [
      {
        label: t('my_promo_usable_tab'),
        value: 'active',
        contentComponent: PromoCodeList,
        contentComponentProps: {
          promoCodes: activePromoCodes.value,
          isActive: true,
        },
      },
      {
        label: t('my_promo_expired_tab'),
        value: 'inactive',
        contentComponent: PromoCodeList,
        contentComponentProps: {
          promoCodes: inactivePromoCodes.value,
          isActive: false,
        },
      },
    ])

    // add onMounted for user who viewed else page first, then go here
    onMounted(() => {
      if (!userInfo.value) {
        return
      }
      getPromoCodesAndAutoRedeem()
    })

    watch(isSuccessHintShow, newValue => {
      if (newValue) {
        setTimeout(() => {
          isSuccessHintShow.value = false
        }, 5000)
      }
    })

    function hidePromoHint() {
      shouldInputErrorShow.value = false
      errorMessage.value = ''
      isSuccessHintShow.value = false
    }
    function hideSuccessPromoHint() {
      isSuccessHintShow.value = false
    }

    function handlePromoCodeChange() {
      hidePromoHint()
    }

    async function getPromoCodes() {
      const promoCodeObject = await memberService
        .getPromoCodes($axios)
        .then(res => res.data)
        .catch(err => {
          // auth failed
          if (err.code === 11) {
            openLoginDialog()
          }
        })

      if (!promoCodeObject) {
        return
      }
      activePromoCodes.value = promoCodeObject.active
      inactivePromoCodes.value = promoCodeObject.inactive
    }

    async function getPromoCodesAndAutoRedeem() {
      await getPromoCodes()

      const promoCode = $cookiz.get('promoCode') || ''
      if (!promoCode) {
        return
      }
      inputPromoCode.value = promoCode?.toString() || ''
      $cookiz.remove('promoCode')
      const shouldAutoRedeem = $cookiz.get('shouldAutoRedeem') === 1

      if (shouldAutoRedeem) {
        $cookiz.remove('shouldAutoRedeem')
        verifyPromoCode()
      }
    }

    async function verifyPromoCode() {
      isButtonLoading.value = true
      hidePromoHint()

      await memberService
        .verifyPromoCode($axios, { promo_code: inputPromoCode.value })
        .then(res => {
          if (res.code !== 0) {
            return
          }
          isButtonLoading.value = false
          inputPromoCode.value = ''
          isSuccessHintShow.value = true
          verifiedPromoCodeTitle.value = res.data.promo_code.title
          if (res.data.award_type === 1) {
            store.dispatch('authentication/getUserInfo')
            return
          }

          store.dispatch('authentication/getUserInfo')
          getPromoCodes()
        })
        .catch(err => {
          isButtonLoading.value = false
          shouldInputErrorShow.value = true
          errorMessage.value = err.message
        })
    }

    return {
      inputPromoCode,
      handlePromoCodeChange,
      activePromoCodes,
      inactivePromoCodes,
      isButtonLoading,
      shouldDisableClaimButton,
      verifyPromoCode,
      isMobile,
      verifiedPromoCodeTitle,
      isSuccessHintShow,
      hideSuccessPromoHint,
      shouldInputErrorShow,
      errorMessage,
      tabs,
    }
  },
})
</script>

<style scoped lang="scss">
.successHintBlock {
  position: absolute;
  top: 24px;
  right: 64px;
  width: 400px;
  color: $white;
  background: $green70;
  border-radius: 4px;
  /* above footer, footer is 2 */
  z-index: 20;

  @include xxs {
    position: fixed;
    top: auto;
    left: 20px;
    right: 20px;
    bottom: 12px;
    width: auto;
  }
}

#inputArea {
  display: flex;

  .inputArea__button {
    margin-left: 8px;
    width: auto;
    &.disable {
      pointer-events: none;
    }
  }

  @include xxs {
    display: block;

    .inputArea__button {
      margin-left: 0px;
      margin-top: 8px;
      width: 100%;
    }
  }
}
:deep(#inputArea .v-input) {
  max-width: 380px;

  @include xxs {
    max-width: none;
  }
}
:deep(#inputArea .v-input__control) {
  @apply wd-h-10;
}
:deep(#inputArea .v-input__slot) {
  background: $white;
}
:deep(#inputArea .v-text-field__details) {
  margin: 0;
  padding: 0;
}
:deep(#inputArea .v-messages__message) {
  font-size: 13px;
  line-height: 20px;
}
</style>
